import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  RemoveCircle as RemoveCircleIcon,
  Webhook as WebhookIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  AdminEmptyState,
  AdminTableActions,
  AdminTableBodyState,
  AdminTablePanel,
  AuditActor,
  PageShell,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { CustomerWebhook, WebhookService } from "@/services/features/WebhookService";
import { getAuditActorName } from "@/utils/auditActor";
import { formatStoreDateTimeWithTimezone, getBrowserTimezone } from "@/utils/storeTime";

const Webhooks: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const browserTimezone = getBrowserTimezone();
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [webhooks, setWebhooks] = useState<CustomerWebhook[]>([]);
  const webhookService = WebhookService.getInstance();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getBooleanChipSx = (isEnabled: boolean) => ({
    bgcolor: isEnabled
      ? alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.24 : 0.1)
      : "action.hover",
    borderColor: isEnabled ? "primary.main" : "grey.500",
    color: isEnabled ? "primary.main" : "grey.500",
    "& .MuiChip-icon": { color: isEnabled ? "primary.main" : "grey.500" },
  });
  const headerChipSx = {
    bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === "dark" ? 0.24 : 0.1),
    borderColor: "secondary.main",
    color: "secondary.main",
  };

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    webhooks,
    (webhook, column) =>
      ({
        id: webhook.id,
        status: webhook.enabled,
        name: webhook.name,
        headers: webhook.headers?.length,
        includeData: webhook.includeData,
        includeDemo: webhook.includeDemoStores,
        createdBy: getAuditActorName(webhook.createdBy),
        created: webhook.createdAt ? new Date(webhook.createdAt).getTime() : null,
      })[column],
    "id" as
      | "id"
      | "status"
      | "name"
      | "headers"
      | "includeData"
      | "includeDemo"
      | "createdBy"
      | "created",
    locale,
  );
  const [recordPage, setRecordPage] = useState(0);
  const [recordPageSize, setRecordPageSize] = useState(25);
  const currentRecordPage = Math.min(
    recordPage,
    Math.max(0, Math.ceil(sortedItems.length / recordPageSize) - 1),
  );
  const displayedRecords = sortedItems.slice(
    currentRecordPage * recordPageSize,
    (currentRecordPage + 1) * recordPageSize,
  );
  useEffect(() => {
    setRecordPage(0);
  }, [sortColumn, sortDirection]);
  const renderRecordPagination = () => (
    <LocalizedTablePagination
      component="div"
      count={sortedItems.length}
      page={currentRecordPage}
      rowsPerPage={recordPageSize}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, next) => setRecordPage(next)}
      onRowsPerPageChange={(event) => {
        setRecordPageSize(Number(event.target.value));
        setRecordPage(0);
      }}
    />
  );

  useEffect(() => {
    document.title = t("management.webhooks.webhooks.documentTitle");
    getAllWebhooks();
  }, [t]);

  useEffect(() => {
    // Check for navigation state (success message from create/edit pages)
    if (location.state) {
      const { successMessage: navSuccessMessage } = location.state;
      if (navSuccessMessage) {
        enqueueSnackbar(navSuccessMessage, { variant: "success" });
        // Clear the state to prevent showing message on refresh
        void navigate(location.pathname, { replace: true });
      }
    }
  }, [enqueueSnackbar, location.pathname, location.state, navigate]);

  const getAllWebhooks = async (showLoading: boolean = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      const response = await webhookService.getAllWebhooks();
      if (response.success && response.data) {
        setWebhooks(response.data);
      } else {
        console.error("Failed to fetch webhooks:", response.message);
        setWebhooks([]);
      }
    } catch (error) {
      console.error("Error fetching webhooks:", error);
      setWebhooks([]);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  const handleCreateWebhook = () => {
    navigate("/admin/integrations/webhooks/create");
  };

  // Edit webhook function - navigate to edit page
  const handleEditWebhook = (webhook: CustomerWebhook) => {
    navigate(`/admin/integrations/webhooks/edit/${webhook.id}`);
  };

  const refreshWebhooksList = async () => {
    console.log("Force refreshing webhooks list");
    try {
      const response = await webhookService.getAllWebhooks();

      if (response.success && response.data) {
        setWebhooks(() => [...response.data]);
      }
    } catch (error) {
      console.error("Error in force refresh:", error);
    }
  };

  const handleToggleWebhookStatus = async (webhook: CustomerWebhook) => {
    try {
      const response = await webhookService.setWebhookEnabled(webhook.id, !webhook.enabled);
      if (response.success) {
        enqueueSnackbar(
          t("management.webhooks.webhooks.messages.webhookStatusUpdated", {
            status: !webhook.enabled
              ? t("management.webhooks.webhooks.status.enabledPast")
              : t("management.webhooks.webhooks.status.disabledPast"),
          }),
          { variant: "success" },
        );

        await refreshWebhooksList();
      } else {
        enqueueSnackbar(
          response.message || t("management.webhooks.webhooks.errors.failedUpdateWebhookStatus"),
          { variant: "error" },
        );
      }
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error
          ? error.message
          : t("management.webhooks.webhooks.errors.failedUpdateWebhookStatus"),
        { variant: "error" },
      );
    }
  };

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.webhooks.webhooks.title")}
      icon={<WebhookIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateWebhook}
          startIcon={<AddIcon />}
          disabled={isLoading}
          size={isMobile ? "small" : "medium"}
          sx={{
            py: { xs: 0.75, sm: 1 },
            "& .MuiButton-startIcon": {
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        >
          {t("management.webhooks.webhooks.actions.addWebhook")}
        </Button>
      }
    >
      {/* Content Area */}
      <Box>
        <AdminTablePanel
          header={renderRecordPagination()}
          footer={renderRecordPagination()}
          mobileContent={
            <Box
              sx={{
                minWidth: 0,
                "& .MuiTypography-root": {
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }}
            >
              <RecordSortControl
                value={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
                options={[
                  { value: "id" as const, label: t("management.webhooks.webhooks.fields.id") },
                  {
                    value: "status" as const,
                    label: t("management.webhooks.webhooks.fields.status"),
                  },
                  { value: "name" as const, label: t("management.webhooks.webhooks.fields.name") },
                  {
                    value: "headers" as const,
                    label: t("management.webhooks.webhooks.fields.headers"),
                  },
                  {
                    value: "includeData" as const,
                    label: t("management.webhooks.webhooks.fields.includeData"),
                  },
                  {
                    value: "includeDemo" as const,
                    label: t("management.webhooks.webhooks.fields.includeDemo"),
                  },
                  {
                    value: "createdBy" as const,
                    label: t("management.webhooks.webhooks.fields.createdBy"),
                  },
                  {
                    value: "created" as const,
                    label: t("management.webhooks.webhooks.fields.created"),
                  },
                ]}
              />
              <RecordList
                items={displayedRecords}
                getItemId={(webhook) => webhook.id}
                loading={isLoading}
                resetKey={JSON.stringify([
                  sortColumn,
                  sortDirection,
                  ...displayedRecords.map((webhook) => webhook.id),
                ])}
                renderTitle={(webhook) => {
                  return (
                    <>
                      <Typography variant="body2" fontWeight="medium">
                        {webhook.name}
                      </Typography>
                    </>
                  );
                }}
                renderSummary={(webhook) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("management.webhooks.webhooks.fields.status")}</>,
                          value: (
                            <>
                              <Tooltip
                                title={t("management.webhooks.webhooks.tooltips.clickWebhook", {
                                  value: webhook.enabled
                                    ? t("management.webhooks.webhooks.actions.disable")
                                    : t("management.webhooks.webhooks.actions.enable"),
                                })}
                              >
                                <IconButton
                                  aria-label={
                                    webhook.enabled
                                      ? t("management.webhooks.webhooks.actions.disable")
                                      : t("management.webhooks.webhooks.actions.enable")
                                  }
                                  color={webhook.enabled ? "success" : "error"}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    void handleToggleWebhookStatus(webhook);
                                  }}
                                  size="small"
                                  sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                                >
                                  {webhook.enabled ? <CheckCircleIcon /> : <CancelIcon />}
                                </IconButton>
                              </Tooltip>
                            </>
                          ),
                        },
                        {
                          label: <>{t("management.webhooks.webhooks.fields.headers")}</>,
                          value: (
                            <>
                              {webhook.headers && webhook.headers.length > 0 ? (
                                <Tooltip
                                  title={
                                    <Box>
                                      {webhook.headers.map((header, index) => (
                                        <Typography key={index} variant="caption" display="block">
                                          <strong>{header.key}:</strong> {header.value}
                                        </Typography>
                                      ))}
                                    </Box>
                                  }
                                >
                                  <Chip
                                    label={t("management.webhooks.webhooks.summary.headerCount", {
                                      count: webhook.headers.length,
                                    })}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ ...headerChipSx, fontSize: "0.75rem" }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {t("management.webhooks.webhooks.empty.none")}
                                </Typography>
                              )}
                            </>
                          ),
                        },
                      ]}
                    />
                  );
                }}
                renderDetails={(webhook) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("management.webhooks.webhooks.fields.id")}</>,
                          value: <>{webhook.id}</>,
                        },
                        {
                          label: <>{t("management.webhooks.webhooks.fields.includeData")}</>,
                          value: (
                            <>
                              <Chip
                                label={webhook.includeData ? t("common.yes") : t("common.no")}
                                icon={
                                  webhook.includeData ? <CheckCircleIcon /> : <RemoveCircleIcon />
                                }
                                color={webhook.includeData ? "primary" : "default"}
                                size="small"
                                variant="outlined"
                                sx={getBooleanChipSx(webhook.includeData)}
                              />
                            </>
                          ),
                        },
                        {
                          label: <>{t("management.webhooks.webhooks.fields.includeDemo")}</>,
                          value: (
                            <>
                              <Chip
                                label={webhook.includeDemoStores ? t("common.yes") : t("common.no")}
                                icon={
                                  webhook.includeDemoStores ? (
                                    <CheckCircleIcon />
                                  ) : (
                                    <RemoveCircleIcon />
                                  )
                                }
                                color={webhook.includeDemoStores ? "primary" : "default"}
                                size="small"
                                variant="outlined"
                                sx={getBooleanChipSx(webhook.includeDemoStores)}
                              />
                            </>
                          ),
                        },
                        {
                          label: <>{t("management.webhooks.webhooks.fields.createdBy")}</>,
                          value: (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                <AuditActor value={webhook.createdBy} />
                              </Typography>
                            </>
                          ),
                        },
                        {
                          label: <>{t("management.webhooks.webhooks.fields.created")}</>,
                          value: (
                            <>
                              <Typography variant="body2">
                                {formatStoreDateTimeWithTimezone(
                                  webhook.createdAt,
                                  browserTimezone,
                                  locale,
                                )}
                              </Typography>
                            </>
                          ),
                        },
                      ]}
                    />
                  );
                }}
                renderActions={(webhook) => (
                  <AdminTableActions
                    actions={[
                      {
                        icon: <EditIcon />,
                        hint: t("management.webhooks.webhooks.tooltips.editWebhook"),
                        action: () => handleEditWebhook(webhook),
                      },
                    ]}
                  />
                )}
                emptyState={
                  <AdminEmptyState
                    icon={<WebhookIcon sx={{ fontSize: 40 }} />}
                    text={t("management.webhooks.webhooks.empty.noWebhooksFound")}
                  />
                }
              />
            </Box>
          }
        >
          <TableHead>
            <TableRow>
              <SortableTableCell
                activeColumn={sortColumn}
                column="id"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.id")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="status"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.status")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="name"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.name")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="headers"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.headers")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="includeData"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.includeData")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="includeDemo"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.includeDemo")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="createdBy"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.createdBy")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={sortColumn}
                column="created"
                direction={sortDirection}
                onSort={handleSort}
              >
                {t("management.webhooks.webhooks.fields.created")}
              </SortableTableCell>
              <TableCell>{t("management.webhooks.webhooks.fields.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <AdminTableBodyState
            items={displayedRecords}
            isLoading={isLoading}
            colSpan={9}
            loadingLabel={t("common.loading")}
            emptyState={
              <AdminEmptyState
                icon={<WebhookIcon sx={{ fontSize: 40 }} />}
                text={t("management.webhooks.webhooks.empty.noWebhooksFound")}
              />
            }
          >
            {(webhook) => (
              <TableRow
                key={webhook.id}
                hover
                role="button"
                tabIndex={0}
                onClick={() => handleEditWebhook(webhook)}
                onKeyDown={(event) => {
                  if (event.target !== event.currentTarget) {
                    return;
                  }
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleEditWebhook(webhook);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  "&&:hover, &&:hover > .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>{webhook.id}</TableCell>
                <TableCell>
                  <Tooltip
                    title={t("management.webhooks.webhooks.tooltips.clickWebhook", {
                      value: webhook.enabled
                        ? t("management.webhooks.webhooks.actions.disable")
                        : t("management.webhooks.webhooks.actions.enable"),
                    })}
                  >
                    <IconButton
                      aria-label={
                        webhook.enabled
                          ? t("management.webhooks.webhooks.actions.disable")
                          : t("management.webhooks.webhooks.actions.enable")
                      }
                      color={webhook.enabled ? "success" : "error"}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleToggleWebhookStatus(webhook);
                      }}
                      size="small"
                      sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                    >
                      {webhook.enabled ? <CheckCircleIcon /> : <CancelIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {webhook.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  {webhook.headers && webhook.headers.length > 0 ? (
                    <Tooltip
                      title={
                        <Box>
                          {webhook.headers.map((header, index) => (
                            <Typography key={index} variant="caption" display="block">
                              <strong>{header.key}:</strong> {header.value}
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      <Chip
                        label={t("management.webhooks.webhooks.summary.headerCount", {
                          count: webhook.headers.length,
                        })}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ ...headerChipSx, fontSize: "0.75rem" }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("management.webhooks.webhooks.empty.none")}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={webhook.includeData ? t("common.yes") : t("common.no")}
                    icon={webhook.includeData ? <CheckCircleIcon /> : <RemoveCircleIcon />}
                    color={webhook.includeData ? "primary" : "default"}
                    size="small"
                    variant="outlined"
                    sx={getBooleanChipSx(webhook.includeData)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={webhook.includeDemoStores ? t("common.yes") : t("common.no")}
                    icon={webhook.includeDemoStores ? <CheckCircleIcon /> : <RemoveCircleIcon />}
                    color={webhook.includeDemoStores ? "primary" : "default"}
                    size="small"
                    variant="outlined"
                    sx={getBooleanChipSx(webhook.includeDemoStores)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    <AuditActor value={webhook.createdBy} />
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatStoreDateTimeWithTimezone(webhook.createdAt, browserTimezone, locale)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <AdminTableActions
                    actions={[
                      {
                        icon: <EditIcon />,
                        hint: t("management.webhooks.webhooks.tooltips.editWebhook"),
                        action: () => handleEditWebhook(webhook),
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            )}
          </AdminTableBodyState>
        </AdminTablePanel>
      </Box>
    </PageShell>
  );
};

export default Webhooks;
